import React, { useCallback, useEffect, useState } from 'react';
import Filters from './Filters';
import defaultInstance from '../../axiosHelper';
import { API_ENDPOINTS } from '../../utils/apiEndPoints';
import FamilyMembers from './FamilyMembers';
import { ROLE_TYPE } from '../../utils/helper';
import UpdateMem from './UpdateMem';
import { getSessionFilter, setSessionFilter } from '../../utils/sessionStorage';

const Users = () => {

    // Initialize state from sessionStorage or use defaults
    const [filterParams, setFilterParams] = useState(() => {
        return getSessionFilter('userFilter') || {
            page_limit: 50,
            skip_count: 0,
            role_type: ''
        };
    });

    const [allData, setAllData] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [selectedItem, setSelectedItem] = useState(null);
    const [showMemModal, setShowMemModal] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    const fetchMasterList = async () => {
        try {
            const response = await defaultInstance.get(API_ENDPOINTS.TEST_USER_MASTER_LIST, { params: filterParams });
            if (response?.data?.success) {
                setAllData(response?.data?.data?.length > 0 ? response?.data?.data : []);
                setTotalCount(response?.data?.total_count || 0);
            }
        } catch (error) {
            console.error('Error fetching user list:', error);
        } finally {
            setIsLoading(false);
        }
    }

    const handlePaginate = (type) => {
        const { page_limit = 50, skip_count = 0 } = filterParams;
        let newSkipCount = Number(skip_count);

        if (type === 'N') {
            newSkipCount += 50;
            if (newSkipCount >= totalCount) {
                newSkipCount = totalCount - (totalCount % page_limit);
            }
        } else if (type === 'P') {
            newSkipCount -= 50;
            if (newSkipCount < 0) {
                newSkipCount = 0;
            }
        }

        // Update filter params with new pagination values
        const updatedParams = {
            ...filterParams,
            page_limit,
            skip_count: newSkipCount
        };

        setFilterParams(updatedParams);
        setSessionFilter('userFilter', updatedParams);
    };

    // Commented out as it's not currently used but might be needed later
    // const validateDateRange = (data) => {
    //     const fromDate = moment(data?.from_date, 'YYYY-MM-DD');
    //     const toDate = moment(data?.to_date, 'YYYY-MM-DD');

    //     // Ensure the dates are valid
    //     if (!fromDate?.isValid() || !toDate?.isValid()) {
    //         return "Invalid date format. Please use YYYY-MM-DD.";
    //     }

    //     // Calculate the difference in days
    //     const dateDifference = toDate?.diff(fromDate, 'days');

    //     // Check if the difference exceeds 30 days
    //     if (dateDifference > 30) {
    //         return "The date range exceeds 30 days. Please select a range within 30 days.";
    //     }

    //     return null;
    // }

    const onExport = async () => {
        // const error = validateDateRange(filterParams);
        // if (error) {
        //     alert(error);
        //     return;
        // }

        const response = await defaultInstance.get(API_ENDPOINTS.TEST_EXPORT_MASTER_LIST, { params: filterParams });
        const blob = new Blob([response?.data], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        link.href = url;
        link.download = "data.csv";
        link.click();
        URL.revokeObjectURL(url);
    }

    // Memoized function to fetch data when filter params change
    const fetchMasterListMemoized = useCallback(() => {
        const fetchData = async () => {
            try {
                const response = await defaultInstance.get(API_ENDPOINTS.TEST_USER_MASTER_LIST, {
                    params: filterParams
                });
                if (response?.data?.success) {
                    setAllData(response?.data?.data?.length > 0 ? response?.data?.data : []);
                    setTotalCount(response?.data?.total_count || 0);
                }
            } catch (error) {
                console.error('Error fetching user list:', error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchData();
    }, [filterParams]);

    useEffect(() => {
        fetchMasterListMemoized();
    }, [fetchMasterListMemoized]);

    // Listen for storage events to handle changes from other tabs/windows
    useEffect(() => {
        const handleStorageChange = (e) => {
            if (e.key === 'userFilter') {
                const savedFilter = getSessionFilter('userFilter');
                if (savedFilter) {
                    setFilterParams(savedFilter);
                }
            }
        };

        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, []);

    const showEditAction = filterParams?.role_type === ROLE_TYPE.ZONAL_MANAGER;
    const viewingCount = Number(filterParams?.skip_count || 0) + 50;

    if (isLoading) {
        return (<div style={{ width: '98vw', height: '95vh', position: 'absolute', alignItems: 'center', justifyContent: 'center', display: 'flex' }}>
            <img style={{ width: 50, height: 50 }} src='./loading.gif' alt='loading' />
        </div>)
    }

    return (
        <div className="role-container">
            <Filters
                onFilterChange={(newFilters) => {
                    const updated = { ...filterParams, ...newFilters, skip_count: 0 };
                    setFilterParams(updated);
                    setSessionFilter('userFilter', updated);
                }}
                totalCount={totalCount}
                viewingCount={viewingCount}
                onExport={onExport}
                onEdit={() => setShowMemModal(true)}
                selectedItem={selectedItem}
                onDone={fetchMasterListMemoized}
            />
            <FamilyMembers
                data={allData}
                selectedItem={selectedItem}
                onChange={(mem) => setSelectedItem(selectedItem?.fm_id === mem?.fm_id ? null : mem)}
                showEditAction={showEditAction}
            />
            <div className='custom-pagination'>
                {totalCount ? (
                    <p style={{ marginRight: '5px' }}>
                        {`Showing ${Number(filterParams?.skip_count || 0) + 1} to ${viewingCount > totalCount ? totalCount : viewingCount} of ${totalCount} entries`}
                    </p>
                ) : null}
                <div className="option-container">
                    <button
                        disabled={Number(filterParams?.skip_count || 0) <= 0}
                        onClick={() => handlePaginate('P')}
                        className='pagination-button'
                    >
                        Previous
                    </button>
                    <button
                        disabled={viewingCount >= totalCount}
                        onClick={() => handlePaginate('N')}
                        className='pagination-button'
                    >
                        Next
                    </button>
                </div>
            </div>
            <UpdateMem
                visible={showMemModal}
                onDismiss={() => setShowMemModal(false)}
                data={selectedItem}
                onDone={() => {
                    setShowMemModal(false);
                    setSelectedItem(null);
                    fetchMasterListMemoized();
                }}
            />
        </div>
    )
}

export default Users
